import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import appVersionInfo from '../version.json';
import { alpha } from '@mui/material/styles';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Link as RouterLink } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import RunSnapshotWidget from '../components/RunSnapshotWidget';
import {
  BAG_PRESET_OPTIONS,
  BAG_PRESETS,
  DEFAULT_BAG_PRESET,
} from '../features/scrabble/config/gameConfig';
import { isAdminEnabled } from '../config/adminAccess';
import {
  GAME_STATE_CHANGED_EVENT,
  PLAYER_STATS_CHANGED_EVENT,
  getStoredGameState,
  getStoredPlayerStats,
  getStoredPlayerState,
  writeStoredPlayerState,
} from '../features/scrabble/services/localStorageService';
import {
  SETTINGS_CHANGED_EVENT,
  getHomeWidgetEnabledIds,
} from '../features/scrabble/services/settingsService';
import { HOME_WIDGETS, HOME_WIDGET_DEFAULT_ENABLED_IDS } from '../config/homeWidgets';
import { createGlimmerButtonSx } from '../utils/glimmerButtonSx';

const resumeButtonSx = createGlimmerButtonSx({ animationName: 'homeResumeButtonGlimmer', duration: 3.7 });

const EMPTY_HOME_STATS = {
  lifetimePoints: '--',
  wins: '--',
  wordsSubmitted: '--',
  averageWordScore: '--',
};

const getStoredHomeStats = () => {
  try {
    const stored = getStoredPlayerStats();

    if (!stored || typeof stored !== 'object') {
      return EMPTY_HOME_STATS;
    }

    const lifetimePoints = Number(stored.scoring?.lifetimePoints || 0);
    const wins = Number(stored.runs?.won || 0);
    const wordsSubmitted = Number(stored.scoring?.lifetimeSubmittedWords || 0);
    const averageWordScore = Number(stored.scoring?.averageWordScore || 0);

    return {
      lifetimePoints: Number.isFinite(lifetimePoints) ? lifetimePoints : '--',
      wins: Number.isFinite(wins) ? wins : '--',
      wordsSubmitted: Number.isFinite(wordsSubmitted) ? wordsSubmitted : '--',
      averageWordScore:
        Number.isFinite(averageWordScore) && wordsSubmitted > 0
          ? Math.round(averageWordScore * 10) / 10
          : '--',
    };
  } catch (error) {
    return EMPTY_HOME_STATS;
  }
};

function HomePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = React.useState(() => getStoredHomeStats());
  const [hasActiveRun, setHasActiveRun] = React.useState(() => Boolean(getStoredGameState()));
  const [selectedBagPreset, setSelectedBagPreset] = React.useState(() => {
    const storedPlayerState = getStoredPlayerState();
    return storedPlayerState?.bagPreset || DEFAULT_BAG_PRESET;
  });

  const [enabledWidgetIds, setEnabledWidgetIds] = React.useState(() =>
    getHomeWidgetEnabledIds(HOME_WIDGET_DEFAULT_ENABLED_IDS)
  );
  const [widgetCycleIndex, setWidgetCycleIndex] = React.useState(() => {
    const ids = getHomeWidgetEnabledIds(HOME_WIDGET_DEFAULT_ENABLED_IDS);
    const count = HOME_WIDGETS.filter((w) => ids.includes(w.id)).length;
    return count > 0 ? Math.floor(Math.random() * count) : 0;
  });
  const [fullStats, setFullStats] = React.useState(() => getStoredPlayerStats());

  const enabledWidgets = React.useMemo(
    () => HOME_WIDGETS.filter((w) => enabledWidgetIds.includes(w.id)),
    [enabledWidgetIds]
  );

  const currentWidget = enabledWidgets.length > 0
    ? enabledWidgets[widgetCycleIndex % enabledWidgets.length]
    : null;


  React.useEffect(() => {
    const syncHomeState = () => {
      setStats(getStoredHomeStats());
      setHasActiveRun(Boolean(getStoredGameState()));
      setFullStats(getStoredPlayerStats());
      const storedPlayerState = getStoredPlayerState();
      if (storedPlayerState?.bagPreset) {
        setSelectedBagPreset(storedPlayerState.bagPreset);
      }
    };

    const syncWidgetSettings = () => {
      const newIds = getHomeWidgetEnabledIds(HOME_WIDGET_DEFAULT_ENABLED_IDS);
      setEnabledWidgetIds(newIds);
      const count = HOME_WIDGETS.filter((w) => newIds.includes(w.id)).length;
      setWidgetCycleIndex(count > 0 ? Math.floor(Math.random() * count) : 0);
    };

    syncHomeState();

    if (typeof window === 'undefined') {
      return undefined;
    }

    window.addEventListener(GAME_STATE_CHANGED_EVENT, syncHomeState);
    window.addEventListener(PLAYER_STATS_CHANGED_EVENT, syncHomeState);
    window.addEventListener('storage', syncHomeState);
    window.addEventListener(SETTINGS_CHANGED_EVENT, syncWidgetSettings);

    return () => {
      window.removeEventListener(GAME_STATE_CHANGED_EVENT, syncHomeState);
      window.removeEventListener(PLAYER_STATS_CHANGED_EVENT, syncHomeState);
      window.removeEventListener('storage', syncHomeState);
      window.removeEventListener(SETTINGS_CHANGED_EVENT, syncWidgetSettings);
    };
  }, []);

  const selectedPresetMeta =
    BAG_PRESET_OPTIONS.find((preset) => preset.id === selectedBagPreset) ||
    BAG_PRESET_OPTIONS.find((preset) => preset.id === DEFAULT_BAG_PRESET) ||
    BAG_PRESET_OPTIONS[0];

  const statCards = [
    {
      label: 'Lifetime Points',
      value: stats.lifetimePoints,
      accent: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
    },
    {
      label: 'Wins',
      value: stats.wins,
      accent: 'linear-gradient(135deg, #b45309 0%, #d97706 100%)',
    },
    {
      label: 'Words Submitted',
      value: stats.wordsSubmitted,
      accent: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
    },
    {
      label: 'Avg Word Score',
      value: stats.averageWordScore,
      accent: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, sm: 3 },
        py: { xs: 3, md: 5 },
        background: 'linear-gradient(180deg, #f8fbff 0%, #f5f7fb 100%)',
      }}
    >
      <Stack spacing={3.5} sx={{ maxWidth: 1180, mx: 'auto' }}>
        <PageTitle subtitle="Altered Tiles" />

        <Grid container spacing={1.5}>
          {statCards.map((stat) => (
            <Grid key={stat.label} size={{ xs: 6, md: 3 }}>
              <Card
                sx={{
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.12)}`,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ height: 5, background: stat.accent }} />
                <CardContent>
                  <Typography
                    variant="h4"
                    sx={{ color: theme.palette.text.primary, fontWeight: 800, lineHeight: 1.1 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, letterSpacing: 0.4 }}
                  >
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 5, lg: 4 }}>
            <Card
              sx={{
                border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                backgroundColor: theme.palette.background.paper,
                boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
              }}
            >
              <CardContent>
                <Stack spacing={1.5}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      if (!hasActiveRun) {
                        const previousPlayerState = getStoredPlayerState() || {};
                        writeStoredPlayerState({
                          ...previousPlayerState,
                          bagPreset: selectedBagPreset,
                        });
                      }
                      navigate('/play');
                    }}
                    sx={{
                      py: 1.7,
                      fontWeight: 700,
                      borderRadius: 2,
                      ...(hasActiveRun
                        ? resumeButtonSx
                        : {
                            background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)',
                            },
                          }),
                    }}
                  >
                    {hasActiveRun ? 'Resume Game' : 'New Game'}
                  </Button>

                  {!hasActiveRun ? (
                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 1.2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.03),
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.75, color: 'text.secondary' }}
                      >
                        Starting Bag
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={selectedBagPreset}
                          onChange={(event) => setSelectedBagPreset(event.target.value)}
                        >
                          {BAG_PRESET_OPTIONS.filter(
                            (preset) => isAdminEnabled() || preset.id !== BAG_PRESETS.TEST.id
                          ).map((preset) => (
                            <MenuItem key={preset.id} value={preset.id}>
                              {preset.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography sx={{ mt: 0.8, fontSize: 12, color: 'text.secondary' }}>
                        {selectedPresetMeta?.description}
                      </Typography>
                    </Box>
                  ) : (
                    <RunSnapshotWidget />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 7, lg: 8 }}>
            {enabledWidgets.length > 0 && currentWidget ? (
              <Box>
                <currentWidget.Component stats={fullStats} />
              </Box>
            ) : (
              <Box
                sx={{
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 3,
                  px: 2.5,
                  py: 4,
                  textAlign: 'center',
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 160,
                }}
              >
                <TuneRoundedIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 1 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  No widgets selected
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Go to{' '}
                  <Box
                    component={RouterLink}
                    to="/settings"
                    sx={{ color: 'primary.main', fontWeight: 700, textDecoration: 'none' }}
                  >
                    Settings
                  </Box>{' '}
                  to choose which stats appear here.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Stack>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textAlign: 'right',
          mt: 2,
          pr: 2,
          pb: 1.5,
          color: 'text.disabled',
          fontSize: 11,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        v{appVersionInfo.version}
      </Typography>
    </Box>
  );
}

export default HomePage;
